/**
 * 管理后台各页共用的小组件：搜索框、带原因的确认弹窗、审计列表、状态徽章、金额格式。
 *
 * 背景：管理后台的写操作（锁账号、改角色、改策略、退款）都要求填原因并写进审计；账号、订单详情页
 *   都要展示「与这个对象有关的审计记录」。
 * 设计意图：原因弹窗统一处理「必填、最少 2 个字、提交中禁用关闭、失败时把错误显示在弹窗里」，各页只关心提交什么。
 * 关键约束：原因长度与后端一致（2–500 字）；审计详情是任意 JSON，列表里只显示一行摘要，悬停看全文。
 */
import { IconSearch } from '@tabler/icons-react'
import { useEffect, useState, type ReactNode } from 'react'
import { fmtUsd } from '@ap/api-client/money'
import type { AuditItem, Page } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { C, errorText } from '@ap/i18n/common'
import { intlTag, useI18n } from '@ap/i18n'
import {
  Badge,
  Button,
  Field,
  Form,
  Input,
  LoadMore,
  Modal,
  Notice,
  Table,
  Textarea,
  fmtDateTime,
  usePaged,
  type Column,
  type Tone,
} from '@ap/ui'

export function useUsd() {
  const { locale } = useI18n()
  const tag = intlTag(locale)
  return (units: number | null | undefined, digits = 2) => fmtUsd(units ?? 0, tag, digits)
}

export function SearchBar({ initial, placeholder, onSearch }: { initial: string; placeholder: string; onSearch: (q: string) => void }) {
  const { t } = useI18n()
  const [q, setQ] = useState(initial)
  useEffect(() => setQ(initial), [initial])
  return (
    <Form onSubmit={() => onSearch(q.trim())} className="ap-row">
      <Input style={{ flex: '1 1 280px', maxWidth: 420 }} placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
      <Button type="submit" icon={<IconSearch size={16} />}>
        {t(C.search)}
      </Button>
    </Form>
  )
}

const STATUS_TONE: Record<string, Tone> = {
  active: 'success',
  enabled: 'success',
  locked: 'danger',
  suspended: 'danger',
  deleted: 'neutral',
  disabled: 'neutral',
}

export function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n()
  const entry = A.status[status]
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{entry ? t(entry) : status}</Badge>
}

/**
 * 需要填原因的操作弹窗。children 放额外的输入项（金额、角色等）；onSubmit 抛错时错误显示在弹窗里，不关闭。
 */
export function ReasonModal({
  open,
  title,
  lead,
  confirmLabel,
  danger,
  reasonLabel,
  children,
  onSubmit,
  onClose,
}: {
  open: boolean
  title: ReactNode
  lead?: ReactNode
  confirmLabel?: ReactNode
  danger?: boolean
  reasonLabel?: ReactNode
  children?: ReactNode
  onSubmit: (reason: string) => Promise<void>
  onClose: () => void
}) {
  const { t, locale } = useI18n()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setReason('')
      setError('')
    }
  }, [open])

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      await onSubmit(reason.trim())
    } catch (e) {
      setError(errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} busy={busy} title={title}>
      <Form onSubmit={submit}>
        {lead && <Notice tone={danger ? 'warning' : 'accent'}>{lead}</Notice>}
        {error && <Notice tone="danger">{error}</Notice>}
        {children}
        <Field label={reasonLabel ?? t(C.reason)} hint={t(C.reasonHint)}>
          <Textarea required minLength={2} maxLength={500} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant={danger ? 'danger' : 'primary'} loading={busy}>
            {confirmLabel ?? t(C.confirm)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

function summary(detail: Record<string, unknown>): string {
  const s = JSON.stringify(detail ?? {})
  return s === '{}' ? '' : s
}

/** 审计列表：fetchPage 用 id 作游标，按 id 倒序。 */
export function AuditList({
  fetchPage,
  deps,
}: {
  fetchPage: (cursor: number | null) => Promise<Page<AuditItem, number>>
  deps: unknown[]
}) {
  const { t, locale } = useI18n()
  const list = usePaged<AuditItem, number>(fetchPage, deps)
  const cols: Column<AuditItem>[] = [
    { key: 'at', title: t(C.time), nowrap: true, muted: true, render: (a) => fmtDateTime(a.created_at, locale) },
    { key: 'action', title: t(A.audit.action), nowrap: true, render: (a) => <span className="ap-mono">{a.action}</span> },
    {
      key: 'actor',
      title: t(A.audit.actor),
      render: (a) => (
        <span className="ap-small">
          {a.actor_kind}
          <span className="ap-faint"> · {a.actor_id.slice(0, 8)}</span>
        </span>
      ),
    },
    { key: 'target', title: t(A.audit.target), render: (a) => <span className="ap-mono">{a.target}</span> },
    {
      key: 'detail',
      title: t(A.audit.detail),
      render: (a) => {
        const s = summary(a.detail)
        return (
          <span className="ap-mono ap-muted" title={s} style={{ display: 'inline-block', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
            {s}
          </span>
        )
      },
    },
    { key: 'ip', title: 'IP', nowrap: true, muted: true, render: (a) => a.ip },
  ]
  return (
    <>
      {list.error ? (
        <div className="ap-card__body">
          <Notice tone="danger">{errorText(list.error, locale)}</Notice>
        </div>
      ) : (
        <Table columns={cols} rows={list.items} loading={list.loading} rowKey={(a) => String(a.id)} />
      )}
      <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
    </>
  )
}

/** identity 的审计接口用 before_id 翻页、不返回 next_cursor：满页时把最后一条的 id 当游标。 */
export async function identityAuditPage(
  fetch: (beforeId: number | null) => Promise<{ data: AuditItem[] }>,
  cursor: number | null,
  pageSize = 50,
): Promise<Page<AuditItem, number>> {
  const r = await fetch(cursor)
  const last = r.data.at(-1)
  return { data: r.data, next_cursor: r.data.length >= pageSize && last ? last.id : null }
}
